import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import {
  Banknote,
  CalendarClock,
  CircleDollarSign,
  LockKeyhole,
  Plus,
  ReceiptText,
  Trash2,
  WalletCards,
} from 'lucide-react'
import { useState } from 'react'

import { ApiError, errorMessage } from '@/api/errors'
import {
  useAddSalePayment,
  useIssueFiscalDocument,
  useRefundPayment,
  useReversePayment,
  useSale,
  useSaleInstallments,
  useSetSaleInstallments,
  useVoidSale,
  useCashMutations,
  useCashSession,
  useCashSessions,
  useCurrentCashSession,
  useCurrentPermissions,
  useSales,
} from '@/api/queries'
import type { Payment, PaymentMethod, SaleInstallment, SaleListItem, Uuid } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { SaleDialog } from '@/features/sales/sale-dialog'
import { formatDate, formatDateTime, formatMoney } from '@/lib/format'
import { parseMoneyInput } from '@/lib/money'
import { notify } from '@/lib/toast'

export const Route = createFileRoute('/_app/vendite/')({ component: SalesPage })

const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  cash: 'Contanti',
  card_present: 'Carta / POS',
  card_online: 'Carta online',
  bank_transfer: 'Bonifico',
  sdd: 'SDD',
  voucher: 'Voucher',
  cheque: 'Assegno',
  internal_credit: 'Credito interno',
  store: 'Store',
}

function SalesPage() {
  const [selling, setSelling] = useState(false)
  const permissions = useCurrentPermissions()
  const canSell = permissions.has('sale:write')
  return (
    <>
      <PageHeader
        title="Vendite e cassa"
        description="Vendite atomiche, incassi e quadratura della giornata al banco."
        actions={
          canSell ? (
            <Button onClick={() => setSelling(true)}>
              <Plus /> Nuova vendita
            </Button>
          ) : null
        }
      />
      <Tabs variant="pill" defaultValue="sales">
        <TabsList>
          <TabsTrigger value="sales">
            <ReceiptText /> Vendite
          </TabsTrigger>
          <TabsTrigger value="cash">
            <Banknote /> Cassa
          </TabsTrigger>
        </TabsList>
        <TabsContent value="sales">
          <SalesPanel />
        </TabsContent>
        <TabsContent value="cash">
          <CashPanel />
        </TabsContent>
      </Tabs>
      {selling ? <SaleDialog open={selling} onOpenChange={setSelling} /> : null}
    </>
  )
}

function SalesPanel() {
  const [outstandingOnly, setOutstandingOnly] = useState(false)
  const [collecting, setCollecting] = useState<SaleListItem | null>(null)
  const [opened, setOpened] = useState<Uuid | null>(null)
  const list = useSales({ ...(outstandingOnly ? { outstanding: true } : {}), limit: 100 })
  const rows = list.data?.data ?? []
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">
          Dalla più recente. Un insoluto è una vendita valida con un residuo da incassare.
        </p>
        <Button
          size="sm"
          variant={outstandingOnly ? 'primary' : 'outline'}
          onClick={() => setOutstandingOnly(!outstandingOnly)}
        >
          <CircleDollarSign /> {outstandingOnly ? 'Mostra tutte' : 'Solo insoluti'}
        </Button>
      </div>
      {list.isError && !list.data ? (
        <ErrorState message={errorMessage(list.error)} onRetry={() => void list.refetch()} />
      ) : list.isPending ? (
        <Skeleton className="h-40" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ReceiptText />}
          title={outstandingOnly ? 'Nessun insoluto' : 'Nessuna vendita'}
          description={
            outstandingOnly
              ? 'Tutte le vendite risultano saldate.'
              : 'Registra la prima vendita dal pulsante in alto.'
          }
        />
      ) : (
        <div className="border-border divide-border divide-y rounded-lg border">
          {rows.map((sale) => (
            <div key={sale.id} className="flex flex-wrap items-center gap-3 p-3">
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => setOpened(sale.id)}
              >
                <p className={sale.voidedAt ? 'text-muted-foreground line-through' : 'font-medium'}>
                  {sale.summary}
                </p>
                <p className="text-muted-foreground text-xs">
                  {formatDateTime(sale.soldAt)} ·{' '}
                  {sale.gymMemberId
                    ? `${sale.gymMemberLastName} ${sale.gymMemberFirstName}`
                    : 'senza intestatario'}
                  {sale.soldByName ? ` · ${sale.soldByName}` : ''}
                </p>
              </button>
              <div className="text-right">
                <p className="font-medium" data-numeric>
                  {formatMoney(sale.totalCents, sale.currency)}
                </p>
                <p className="text-muted-foreground text-xs">
                  incassato {formatMoney(sale.paidCents, sale.currency)}
                </p>
              </div>
              {sale.voidedAt ? (
                <Badge variant="neutral">annullata</Badge>
              ) : sale.outstandingCents > 0 ? (
                <>
                  <Badge variant="warning">
                    residuo {formatMoney(sale.outstandingCents, sale.currency)}
                  </Badge>
                  <Button size="sm" onClick={() => setCollecting(sale)}>
                    Incassa
                  </Button>
                </>
              ) : (
                <Badge variant="success">saldata</Badge>
              )}
            </div>
          ))}
        </div>
      )}
      {opened === null ? null : (
        <SaleDetailDialog
          saleId={opened}
          onOpenChange={(open) => {
            if (!open) setOpened(null)
          }}
        />
      )}
      {collecting ? (
        <CollectDialog
          sale={collecting}
          onOpenChange={(open) => {
            if (!open) setCollecting(null)
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * Il dettaglio di una vendita, e i due gesti sul denaro che non avevano un posto.
 *
 * ## Perché non esisteva
 *
 * L'elenco mostrava totali e un pulsante «Incassa»; aprire una vendita non si poteva, e
 * `getSale` stava in `endpoints/sales.ts` senza chiamanti. Senza dettaglio non c'era nemmeno
 * un posto in cui mettere **rimborso** e **storno**, che è la ragione per cui quelle due
 * rotte erano irraggiungibili — un incasso sbagliato al banco restava sbagliato.
 *
 * ## Rimborso e storno sono due gesti, non due nomi dello stesso
 *
 * È la distinzione che questa schermata deve rendere impossibile da confondere, perché il
 * costo di sbagliarla è **misurabile**: usare il rimborso al posto dello storno raddoppia gli
 * incassi lordi (cinquanta incassati, cinquanta rimborsati, cinquanta incassati fanno cento)
 * e mette nella prima nota un'uscita che non è mai uscita.
 *
 * Quindi non un menù con due voci vicine, ma due pulsanti con due frasi sopra: «il denaro
 * torna al cliente» e «l'incasso non è mai avvenuto». La domanda che l'operatore si fa non è
 * «rimborso o storno?» ma «i soldi sono usciti dal cassetto?», ed è quella che le due frasi
 * gli pongono.
 *
 * ## Solo su un incasso `settled`
 *
 * Da un `pending` non è entrato niente, e i pulsanti non compaiono: il server risponderebbe
 * `409 payment_not_settled`, e mostrare un pulsante che risponde `409` è una promessa
 * ritirata dopo il clic — la stessa regola dell'editor degli alimenti comuni.
 */
function SaleDetailDialog({
  saleId,
  onOpenChange,
}: {
  saleId: Uuid
  onOpenChange: (open: boolean) => void
}) {
  const sale = useSale(saleId)
  const refund = useRefundPayment()
  const reverse = useReversePayment()
  const canWrite = useCurrentPermissions().has('sale:write')
  const [acting, setActing] = useState<{ payment: Payment; kind: 'refund' | 'reverse' } | null>(
    null,
  )

  const data = sale.data?.data
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vendita</DialogTitle>
          {data === undefined ? null : (
            <DialogDescription>
              {formatDateTime(data.soldAt)} · {formatMoney(data.totalCents, data.currency)} ·
              incassato {formatMoney(data.paidCents, data.currency)}
            </DialogDescription>
          )}
        </DialogHeader>

        {sale.isPending ? <Skeleton className="h-32 w-full" /> : null}

        {data === undefined ? null : (
          <div className="flex flex-col gap-4">
            <div>
              <h4 className="text-muted-foreground mb-1 text-xs font-medium">Righe</h4>
              <div className="border-border divide-border divide-y rounded-md border text-sm">
                {data.items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-2 p-2">
                    <span className="min-w-0 flex-1 truncate">
                      {item.quantity > 1 ? `${item.quantity}× ` : ''}
                      {item.productName}
                    </span>
                    <span data-numeric>{formatMoney(item.lineTotalCents, data.currency)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-muted-foreground mb-1 text-xs font-medium">Incassi</h4>
              {data.payments.length === 0 ? (
                <p className="text-muted-foreground text-sm">Nessun incasso registrato.</p>
              ) : (
                <div className="border-border divide-border divide-y rounded-md border text-sm">
                  {data.payments.map((payment) => (
                    <div key={payment.id} className="flex flex-wrap items-center gap-2 p-2">
                      <div className="min-w-0 flex-1">
                        <p>
                          {PAYMENT_LABELS[payment.method]}
                          {payment.kind === 'refund' ? ' · rimborso' : ''}
                          {payment.kind === 'reversal' ? ' · storno' : ''}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {formatDateTime(payment.receivedAt)}
                          {payment.collectedByName ? ` · ${payment.collectedByName}` : ''}
                        </p>
                      </div>
                      <span data-numeric>{formatMoney(payment.amountCents, data.currency)}</span>
                      {/*
                        Solo su un incasso `settled` e solo se è un incasso: da un `pending`
                        non è entrato niente, e su un rimborso non c'è nulla da rimborsare.
                        Mostrare i pulsanti e poi rispondere `409` sarebbe una promessa
                        ritirata dopo il clic.
                      */}
                      {payment.kind === 'payment' && payment.status === 'settled' ? (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setActing({ payment, kind: 'refund' })}
                          >
                            Rimborsa
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setActing({ payment, kind: 'reverse' })}
                          >
                            Storna
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Su una vendita annullata il piano si mostra e non si tocca: rinegoziare
                le rate di un documento che non esiste più non vuol dire niente. */}
            <InstallmentsSection
              saleId={saleId}
              currency={data.currency}
              totalCents={data.totalCents}
              canWrite={canWrite && data.voidedAt === null}
            />

            <FiscalSection
              saleId={saleId}
              voided={data.voidedAt !== null}
              paidCents={data.paidCents}
            />

            {/*
              L'annullamento sta in fondo e dietro una conferma con motivo, perché è l'unica
              scrittura su una vendita che non si può disfare: si rifà, non si corregge.
            */}
            {data.voidedAt === null ? (
              <VoidSection
                saleId={saleId}
                etag={sale.data?.etag ?? null}
                paidCents={data.paidCents}
              />
            ) : (
              <Alert variant="neutral">
                Vendita annullata{data.voidReason ? `: ${data.voidReason}` : ''}
                {data.voidedByName ? ` — ${data.voidedByName}` : ''}
              </Alert>
            )}

            {acting === null ? null : (
              <Alert variant={acting.kind === 'refund' ? 'warning' : 'danger'}>
                {/*
                  ⚠️ Le due frasi sono la difesa vera contro la confusione, e vanno lasciate
                  per esteso: la domanda che l'operatore si fa non è «rimborso o storno?» ma
                  «i soldi sono usciti dal cassetto?».
                */}
                {acting.kind === 'refund' ? (
                  <>
                    <strong>Il denaro torna al cliente.</strong> L’incasso resta nel registro e
                    accanto compare un’uscita di pari importo. Usalo quando i soldi escono davvero.
                  </>
                ) : (
                  <>
                    <strong>L’incasso non è mai avvenuto.</strong> Usalo solo se l’hai registrato
                    per errore — metodo, importo o documento sbagliato. Se il denaro è tornato al
                    cliente serve un rimborso, non uno storno.
                  </>
                )}
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          {acting === null ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Chiudi
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setActing(null)}>
                Annulla
              </Button>
              <Button
                disabled={refund.isPending || reverse.isPending}
                onClick={() => {
                  void (async () => {
                    if (acting.kind === 'refund') {
                      await refund.mutateAsync({ paymentId: acting.payment.id })
                    } else {
                      await reverse.mutateAsync({ paymentId: acting.payment.id })
                    }
                    setActing(null)
                  })()
                }}
              >
                {acting.kind === 'refund' ? 'Conferma rimborso' : 'Conferma storno'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Il piano rate — LIS-04.
 *
 * ⚠️ **Una rata è un'aspettativa, non un incasso**, e la schermata lo deve rendere ovvio: il
 * denaro vive negli incassi qui sopra, e queste righe dicono soltanto entro quando quella
 * parte del totale dovrebbe essere coperta. Per questo non c'è nessun pulsante «segna come
 * pagata» — pagare una rata si fa incassando, e un secondo posto in cui il denaro si muove
 * sarebbe la cosa che il contract ha deliberatamente evitato.
 *
 * `coveredCents` e lo stato li calcola il server: ricalcolarli qui produrrebbe due verità su
 * un numero che finisce in una telefonata con chi deve pagare.
 */
function InstallmentsSection({
  saleId,
  currency,
  totalCents,
  canWrite,
}: {
  saleId: Uuid
  currency: string
  totalCents: number
  canWrite: boolean
}) {
  const plan = useSaleInstallments(saleId)
  const [editing, setEditing] = useState(false)
  const rows = plan.data?.data ?? []
  if (plan.isPending) return <Skeleton className="h-16 w-full" />

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <h4 className="text-muted-foreground text-xs font-medium">Piano rate</h4>
        {canWrite ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing((value) => !value)}>
            <CalendarClock className="size-4" />
            {rows.length === 0 ? 'Concorda un piano' : 'Rinegozia'}
          </Button>
        ) : null}
      </div>

      {rows.length === 0 && !editing ? (
        <p className="text-muted-foreground text-xs">
          Nessun piano: l'intero importo è dovuto subito.
        </p>
      ) : null}

      {rows.length > 0 ? (
        <div className="border-border divide-border divide-y rounded-md border text-sm">
          {rows.map((row) => (
            <div key={row.position} className="flex flex-wrap items-center gap-2 p-2">
              <span className="min-w-0 flex-1">
                {row.position}ª · scade il {row.dueOn}
              </span>
              <span data-numeric>{formatMoney(row.amountCents, currency)}</span>
              <Badge variant={INSTALLMENT_VARIANTS[row.status]}>
                {INSTALLMENT_LABELS[row.status]}
              </Badge>
            </div>
          ))}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <p className="text-muted-foreground mt-1 text-xs">
          Le rate non ricevono pagamenti: si coprono incassando, e la più vecchia per prima.
        </p>
      ) : null}

      {editing ? (
        <InstallmentsEditor
          saleId={saleId}
          currency={currency}
          totalCents={totalCents}
          current={rows}
          onDone={() => setEditing(false)}
        />
      ) : null}
    </div>
  )
}

/**
 * Concordare o rinegoziare il piano rate — `PUT /organization/sales/{id}/installments` (LIS-04).
 *
 * ## Perché è l'unica scrittura non append-only di quest'area
 *
 * Un incasso è un fatto avvenuto e non si modifica; un piano rate è un **accordo sul
 * futuro**, e gli accordi si rinegoziano. Il `PUT` sostituisce il piano per intero — non
 * ci sono rate da aggiungere una per una — e un elenco vuoto lo rimuove, che è la ragione
 * per cui il contract non ha una `DELETE`.
 *
 * ## Cosa questa schermata non promette
 *
 * Le rate **non ricevono pagamenti**: il server copre la più vecchia per prima con
 * l'incasso che arriva. Rinegoziare non sposta quindi il denaro già entrato, e una rata
 * già coperta che scompare dal piano nuovo non restituisce niente a nessuno. Il totale
 * delle rate va pareggiato con il totale della vendita, e lo si dice **prima**: il server
 * rifiuta con `422` un piano che non torna, e scoprirlo dopo aver digitato sei date è
 * il modo di far riscrivere tutto.
 */
function InstallmentsEditor({
  saleId,
  currency,
  totalCents,
  current,
  onDone,
}: {
  saleId: Uuid
  currency: string
  totalCents: number
  current: SaleInstallment[]
  onDone: () => void
}) {
  const setPlan = useSetSaleInstallments()
  const [rows, setRows] = useState<{ dueOn: string; amount: string }[]>(() =>
    current.length > 0
      ? current.map((row) => ({
          dueOn: row.dueOn,
          amount: (row.amountCents / 100).toFixed(2).replace('.', ','),
        }))
      : [{ dueOn: new Date().toISOString().slice(0, 10), amount: '' }],
  )

  const cents = rows.map((row) => parseMoneyInput(row.amount) ?? 0)
  const sum = cents.reduce((total, value) => total + value, 0)
  const complete = rows.every((row) => row.dueOn !== '' && parseMoneyInput(row.amount) !== null)
  const balanced = sum === totalCents

  return (
    <div className="border-border mt-2 space-y-2 rounded-md border p-3">
      {rows.map((row, index) => (
        // Indice come chiave: le righe non hanno identità propria, sono posizioni in un
        // elenco che si riscrive intero — è la stessa forma del `PUT`.
        <div key={index} className="flex flex-wrap items-end gap-2">
          <Field label={`${String(index + 1)}ª rata — scade il`}>
            {(props) => (
              <Input
                {...props}
                type="date"
                value={row.dueOn}
                onChange={(e) =>
                  setRows(rows.map((r, i) => (i === index ? { ...r, dueOn: e.target.value } : r)))
                }
              />
            )}
          </Field>
          <Field label="Importo">
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                placeholder="0,00"
                value={row.amount}
                onChange={(e) =>
                  setRows(rows.map((r, i) => (i === index ? { ...r, amount: e.target.value } : r)))
                }
              />
            )}
          </Field>
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Togli la rata"
            disabled={rows.length === 1}
            onClick={() => setRows(rows.filter((_, i) => i !== index))}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setRows([...rows, { dueOn: '', amount: '' }])}
        >
          <Plus className="size-4" />
          Aggiungi una rata
        </Button>
        <span className="text-muted-foreground text-xs tabular-nums">
          {formatMoney(sum, currency)} di {formatMoney(totalCents, currency)}
        </span>
      </div>

      {complete && !balanced ? (
        <Alert variant="warning">
          Le rate sommano {formatMoney(sum, currency)} e la vendita è di{' '}
          {formatMoney(totalCents, currency)}: il server rifiuta un piano che non torna.
        </Alert>
      ) : null}
      {setPlan.isError ? <Alert variant="danger">{errorMessage(setPlan.error)}</Alert> : null}

      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!complete || !balanced}
          loading={setPlan.isPending}
          onClick={() => {
            void (async () => {
              try {
                await setPlan.mutateAsync({
                  saleId,
                  installments: rows.map((row, index) => ({
                    dueOn: row.dueOn,
                    amountCents: cents[index] ?? 0,
                  })),
                })
                notify({ message: 'Piano rate concordato.', variant: 'success' })
                onDone()
              } catch (error) {
                notify({ message: errorMessage(error), variant: 'danger' })
              }
            })()
          }}
        >
          Salva il piano
        </Button>
        {/* Rimuovere il piano è un elenco vuoto, non una `DELETE`: si offre solo dove un
            piano esiste, altrimenti sarebbe un pulsante che non fa niente. */}
        {current.length > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            loading={setPlan.isPending}
            onClick={() => {
              void (async () => {
                try {
                  await setPlan.mutateAsync({ saleId, installments: [] })
                  notify({
                    message: 'Piano rimosso: l’importo è dovuto subito.',
                    variant: 'success',
                  })
                  onDone()
                } catch (error) {
                  notify({ message: errorMessage(error), variant: 'danger' })
                }
              })()
            }}
          >
            Togli il piano
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={onDone}>
          Annulla
        </Button>
      </div>
    </div>
  )
}

const INSTALLMENT_LABELS: Record<SaleInstallment['status'], string> = {
  paid: 'coperta',
  partial: 'in parte',
  due: 'da coprire',
  overdue: 'scaduta',
}
const INSTALLMENT_VARIANTS: Record<
  SaleInstallment['status'],
  'success' | 'warning' | 'neutral' | 'danger'
> = {
  paid: 'success',
  partial: 'warning',
  due: 'neutral',
  overdue: 'danger',
}

/**
 * Emettere ricevuta o fattura — FAT-01.
 *
 * Il numero **non** si indica: lo assegna il registro, e il sezionale ha un default per tipo
 * (`RC` le ricevute, `FT` le fatture). Un sezionale nuovo nasce da solo al primo documento che
 * lo nomina, quindi non c'è niente da creare prima.
 *
 * ⚠️ Su una **fattura** il server pretende i dati fiscali dell'intestatario e senza risponde
 * `409`. Non si nasconde il pulsante: da qui non si sa se quella persona li ha, e nascondere
 * un'azione per un'ipotesi è peggio di un errore che spiega — il messaggio del server dice
 * cosa manca, in italiano.
 */
function FiscalSection({
  saleId,
  voided,
  paidCents,
}: {
  saleId: Uuid
  voided: boolean
  paidCents: number
}) {
  const issue = useIssueFiscalDocument()
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<string | null>(null)
  if (voided) return null
  async function emit(kind: 'receipt' | 'invoice') {
    setError(null)
    try {
      const document = await issue.mutateAsync({ saleId, input: { kind } })
      setIssued(document.fullNumber)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Emissione non riuscita.')
    }
  }
  return (
    <div>
      <h4 className="text-muted-foreground mb-1 text-xs font-medium">Documento fiscale</h4>
      {issued === null ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={issue.isPending}
            onClick={() => void emit('receipt')}
          >
            Emetti ricevuta
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={issue.isPending}
            onClick={() => void emit('invoice')}
          >
            Emetti fattura
          </Button>
          {paidCents === 0 ? (
            <span className="text-muted-foreground text-xs">la vendita non risulta incassata</span>
          ) : null}
        </div>
      ) : (
        <Alert variant="success">Emesso il documento {issued}.</Alert>
      )}
      {error === null ? null : <p className="text-danger-content mt-1 text-xs">{error}</p>}
    </div>
  )
}

/**
 * Annullare una vendita.
 *
 * `reason` è obbligatorio e la schermata non lo aggira: un annullamento senza motivo è un
 * annullamento che nessuno saprà spiegare quando il commercialista chiederà perché il
 * fatturato di marzo è calato.
 *
 * ⚠️ Con un incasso netto diverso da zero il server risponde `409` e la schermata **lo dice
 * prima**, invece di far scoprire l'ordine giusto dall'errore: prima si storna o si rimborsa,
 * poi si annulla — il denaro esce prima dai conti e poi dai documenti.
 */
function VoidSection({
  saleId,
  etag,
  paidCents,
}: {
  saleId: Uuid
  etag: string | null
  paidCents: number
}) {
  const voidSale = useVoidSale()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (!open) {
    return (
      <button
        type="button"
        className="text-muted-foreground self-start text-xs underline"
        onClick={() => setOpen(true)}
      >
        Annulla questa vendita
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <Alert variant="danger">
        <strong>L’annullamento non si disfa.</strong> Una vendita sbagliata si annulla e si rifà:
        correggerne le righe farebbe divergere il totale da ciò che le righe dicono.
      </Alert>
      {paidCents !== 0 ? (
        <Alert variant="warning">
          Questa vendita risulta incassata. Prima storna l’incasso registrato per errore o rimborsa
          il cliente, poi annulla: il denaro esce prima dai conti e poi dai documenti.
        </Alert>
      ) : null}
      <Field label="Motivo" required>
        {(props) => (
          <Input
            {...props}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Prodotto sbagliato, rifatta come annuale"
          />
        )}
      </Field>
      {error === null ? null : <p className="text-danger-content text-xs">{error}</p>}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
          Lascia stare
        </Button>
        <Button
          size="sm"
          disabled={reason.trim().length === 0 || voidSale.isPending}
          onClick={() => {
            void (async () => {
              setError(null)
              try {
                await voidSale.mutateAsync({ saleId, reason: reason.trim(), etag })
                setOpen(false)
              } catch (caught) {
                setError(caught instanceof ApiError ? caught.message : 'Annullamento non riuscito.')
              }
            })()
          }}
        >
          Annulla la vendita
        </Button>
      </div>
    </div>
  )
}

function CollectDialog({
  sale,
  onOpenChange,
}: {
  sale: SaleListItem
  onOpenChange: (open: boolean) => void
}) {
  const payment = useAddSalePayment()
  const cash = useCurrentCashSession()
  const [method, setMethod] = useState<PaymentMethod>('cash')
  const [amount, setAmount] = useState((sale.outstandingCents / 100).toFixed(2).replace('.', ','))
  const cents = parseMoneyInput(amount)
  const cashMissing =
    method === 'cash' && cash.error instanceof ApiError && cash.error.status === 404
  async function submit() {
    if (cents === null || cents <= 0 || cashMissing) return
    await payment.mutateAsync({ saleId: sale.id, input: { method, amountCents: cents } })
    onOpenChange(false)
  }
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registra incasso</DialogTitle>
          <DialogDescription>
            {sale.summary} · residuo {formatMoney(sale.outstandingCents, sale.currency)}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Metodo">
            {(props) => (
              <Select
                {...props}
                value={method}
                onValueChange={(value) => setMethod(value as typeof method)}
                items={Object.entries(PAYMENT_LABELS).map(([value, label]) => ({
                  value: value as PaymentMethod,
                  label,
                }))}
              />
            )}
          </Field>
          <Field
            label="Importo"
            error={
              cents === null
                ? 'Scrivi un importo valido.'
                : cents > sale.outstandingCents
                  ? 'L’importo supera il residuo.'
                  : undefined
            }
          >
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
        </div>
        {cashMissing ? (
          <Alert variant="warning" title="Cassa chiusa">
            Apri la giornata prima di registrare contanti.
          </Alert>
        ) : null}
        {payment.isError ? <Alert variant="danger">{errorMessage(payment.error)}</Alert> : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            loading={payment.isPending}
            disabled={cents === null || cents <= 0 || cents > sale.outstandingCents || cashMissing}
            onClick={() => void submit()}
          >
            Registra incasso
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CashPanel() {
  const current = useCurrentCashSession()
  const history = useCashSessions({ open: false, limit: 50 })
  const permissions = useCurrentPermissions()
  const canWrite = permissions.has('cash_session:write')
  const noCurrent = current.error instanceof ApiError && current.error.status === 404
  const [opening, setOpening] = useState(false)
  const [closing, setClosing] = useState(false)
  const [openedDay, setOpenedDay] = useState<Uuid | null>(null)
  const session = current.data?.data

  return (
    <div className="space-y-4">
      {current.isPending ? (
        <Skeleton className="h-32" />
      ) : session ? (
        <section className="border-success/40 bg-success/5 rounded-lg border p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <Badge variant="success">Cassa aperta</Badge>
              <h2 className="mt-2 font-semibold">
                Giornata del {formatDate(session.businessDate)}
              </h2>
              <p className="text-muted-foreground text-xs">
                Aperta {formatDateTime(session.openedAt)}
                {session.openedByName ? ` da ${session.openedByName}` : ''}
              </p>
            </div>
            {canWrite ? (
              <Button variant="outline" onClick={() => setClosing(true)}>
                <LockKeyhole /> Chiudi e quadra
              </Button>
            ) : null}
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Metric label="Fondo iniziale" cents={session.openingFloatCents} />
            <Metric label="Contante atteso" cents={session.expectedCashCents} emphasized />
            <Metric
              label="Movimenti"
              text={`${session.totalsByMethod.reduce((sum, row) => sum + row.paymentCount, 0)}`}
            />
          </div>
          {session.totalsByMethod.length > 0 ? (
            <div className="border-border mt-4 grid gap-2 border-t pt-3 sm:grid-cols-2 lg:grid-cols-3">
              {session.totalsByMethod.map((row) => (
                <div key={row.method} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {PAYMENT_LABELS[row.method]} · {row.paymentCount}
                  </span>
                  <span data-numeric>{formatMoney(row.netCents)}</span>
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ) : noCurrent ? (
        <section className="border-border bg-card rounded-lg border p-5 text-center">
          <WalletCards className="text-muted-foreground mx-auto size-8" />
          <h2 className="mt-2 font-semibold">Cassa chiusa</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Aprila prima del primo incasso in contanti: il fondo iniziale entra nell'atteso della
            sera.
          </p>
          {canWrite ? (
            <Button className="mt-4" onClick={() => setOpening(true)}>
              <Banknote /> Apri la cassa
            </Button>
          ) : null}
        </section>
      ) : (
        <ErrorState message={errorMessage(current.error)} onRetry={() => void current.refetch()} />
      )}

      <section>
        <h2 className="mb-2 font-semibold">Giornate precedenti</h2>
        {history.isPending ? (
          <Skeleton className="h-24" />
        ) : (history.data?.data.length ?? 0) === 0 ? (
          <p className="text-muted-foreground text-sm">Nessuna chiusura registrata.</p>
        ) : (
          <div className="border-border divide-border divide-y rounded-lg border">
            {history.data?.data.map((row) => (
              <div key={row.id}>
                <button
                  type="button"
                  className="grid w-full items-center gap-2 p-3 text-left sm:grid-cols-[1fr_repeat(3,9rem)]"
                  onClick={() => setOpenedDay(openedDay === row.id ? null : row.id)}
                >
                  <div>
                    <p className="font-medium">{formatDate(row.businessDate)}</p>
                    <p className="text-muted-foreground text-xs">
                      {row.closedAt ? `chiusa ${formatDateTime(row.closedAt)}` : 'aperta'}
                      {row.closedByName ? ` da ${row.closedByName}` : ''}
                    </p>
                  </div>
                  <Metric compact label="Atteso" cents={row.expectedCashCents} />
                  <Metric compact label="Contato" cents={row.countedCashCents} />
                  <Metric
                    compact
                    label="Differenza"
                    cents={row.differenceCents}
                    danger={row.differenceCents !== null && row.differenceCents !== 0}
                  />
                </button>
                {/* La quadratura per metodo si apre, non si riassume: l'elenco non la porta
                    — `CashSessionListItem` la toglie per costruzione — e caricarla per ogni
                    riga sarebbero cinquanta richieste per la giornata che interessa. */}
                {openedDay === row.id ? <CashSessionBreakdown id={row.id} /> : null}
              </div>
            ))}
          </div>
        )}
      </section>
      {opening ? <OpenCashDialog onOpenChange={setOpening} /> : null}
      {closing && session ? (
        <CloseCashDialog
          id={session.id}
          expected={session.expectedCashCents}
          onOpenChange={setClosing}
        />
      ) : null}
    </div>
  )
}

/**
 * La quadratura per metodo di una giornata chiusa — `GET /organization/cash-sessions/{id}`.
 *
 * La differenza fra atteso e contato è un numero; la domanda che segue — «dov'è finito?» —
 * si risponde solo vedendo quanto è passato per contanti e quanto per POS. Un ammanco di
 * venti euro su una giornata in cui i contanti sono ottanta è una cosa; su una in cui sono
 * ottocento è un'altra, e senza questa riga sono lo stesso numero.
 */
function CashSessionBreakdown({ id }: { id: Uuid }) {
  const detail = useCashSession(id)

  if (detail.isPending) return <Skeleton className="mx-3 mb-3 h-16" />
  if (detail.isError)
    return (
      <Alert variant="danger" className="mx-3 mb-3">
        {errorMessage(detail.error)}
      </Alert>
    )

  const data = detail.data
  if (data === undefined) return null

  return (
    <div className="bg-muted/40 mx-3 mb-3 rounded-md p-3">
      {data.totalsByMethod.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          Nessun incasso in questa giornata: il contante atteso è il solo fondo iniziale.
        </p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {data.totalsByMethod.map((row) => (
            <div key={row.method} className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {PAYMENT_LABELS[row.method]} · {row.paymentCount}
              </span>
              <span data-numeric>{formatMoney(row.netCents)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="text-muted-foreground mt-2 flex flex-wrap gap-3 text-xs">
        <span>fondo iniziale {formatMoney(data.openingFloatCents)}</span>
        {data.note === null ? null : <span className="italic">{data.note}</span>}
      </div>
    </div>
  )
}

function Metric({
  label,
  cents,
  text,
  emphasized,
  compact,
  danger,
}: {
  label: string
  cents?: number | null
  text?: string
  emphasized?: boolean
  compact?: boolean
  danger?: boolean
}) {
  return (
    <div className={compact ? 'sm:text-right' : 'bg-card rounded-md p-3'}>
      <p className="text-muted-foreground text-xs">{label}</p>
      <p
        className={`${emphasized ? 'text-xl font-semibold' : 'font-medium'} ${danger ? 'text-danger-content' : ''}`}
        data-numeric
      >
        {text ?? formatMoney(cents)}
      </p>
    </div>
  )
}

function OpenCashDialog({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const { open } = useCashMutations()
  const [float, setFloat] = useState('0,00')
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [note, setNote] = useState('')
  const cents = parseMoneyInput(float)
  async function submit() {
    if (cents === null) return
    await open.mutateAsync({
      openingFloatCents: cents,
      businessDate: date,
      ...(note.trim() ? { note: note.trim() } : {}),
    })
    onOpenChange(false)
  }
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apri la cassa</DialogTitle>
          <DialogDescription>Dichiara il fondo che c'è davvero nel cassetto.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Fondo iniziale"
            error={cents === null ? 'Scrivi un importo valido.' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={float}
                onChange={(e) => setFloat(e.target.value)}
              />
            )}
          </Field>
          <Field label="Giornata contabile">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="Nota" className="sm:col-span-2">
            {(props) => (
              <Textarea {...props} value={note} onChange={(e) => setNote(e.target.value)} />
            )}
          </Field>
        </div>
        {open.isError ? <Alert variant="danger">{errorMessage(open.error)}</Alert> : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            loading={open.isPending}
            disabled={cents === null || date === ''}
            onClick={() => void submit()}
          >
            Apri giornata
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CloseCashDialog({
  id,
  expected,
  onOpenChange,
}: {
  id: string
  expected: number
  onOpenChange: (open: boolean) => void
}) {
  const { close } = useCashMutations()
  const [counted, setCounted] = useState('')
  const [note, setNote] = useState('')
  const cents = parseMoneyInput(counted)
  const difference = cents === null ? null : cents - expected
  async function submit() {
    if (cents === null) return
    await close.mutateAsync({
      id,
      input: { countedCashCents: cents, ...(note.trim() ? { note: note.trim() } : {}) },
    })
    onOpenChange(false)
  }
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Conta e chiudi</DialogTitle>
          <DialogDescription>
            Scrivi il contante reale. Atteso e differenza li calcola e li congela il server.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Metric label="Atteso" cents={expected} />
          <Field
            label="Contante contato"
            required
            error={cents === null && counted !== '' ? 'Scrivi un importo valido.' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                autoFocus
                inputMode="decimal"
                value={counted}
                onChange={(e) => setCounted(e.target.value)}
              />
            )}
          </Field>
        </div>
        {difference !== null ? (
          <Alert
            variant={difference === 0 ? 'success' : 'warning'}
            title={difference === 0 ? 'La cassa quadra' : `Differenza ${formatMoney(difference)}`}
          >
            {difference === 0
              ? 'Contato e atteso coincidono.'
              : 'Spiega la differenza nella nota: è ciò che resterà leggibile domani.'}
          </Alert>
        ) : null}
        <Field label="Nota" required={difference !== null && difference !== 0}>
          {(props) => (
            <Textarea {...props} value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
        {close.isError ? <Alert variant="danger">{errorMessage(close.error)}</Alert> : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            loading={close.isPending}
            disabled={cents === null || (difference !== 0 && note.trim() === '')}
            onClick={() => void submit()}
          >
            <LockKeyhole /> Chiudi cassa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
